import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Typography,
  LinearProgress,
} from "@mui/material";
import { useNotify, useRefresh } from "react-admin";
import { 
  Link as LinkIcon, 
  CheckCircle as CheckCircleIcon, 
  Close as CloseIcon,
  ErrorOutline as ErrorIcon 
} from "@mui/icons-material";
import { extractTextApi } from "../providers/env";
import { useParams, Params } from "react-router-dom";

interface MultiTabURLImportModalProps {
  open: boolean;
  onClose: () => void;
}

const MultiTabURLImportModal: React.FC<MultiTabURLImportModalProps> = ({
  open,
  onClose,
}) => {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [result, setResult] = useState<{
    status: string;
    total_listings?: number;
    total_properties?: number;
    saved: number;
    failed: number;
  } | null>(null);
  
  const notify = useNotify();
  const refresh = useRefresh();
  const params = useParams<Params>();

  // Get agent ID from various sources
  const agentIdFromParams = params?.agentId;
  const agentId =
    agentIdFromParams ??
    (() => {
      try {
        return localStorage.getItem("agentId");
      } catch {
        return undefined;
      }
    })();

  const validateUrl = (url: string): boolean => {
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === "http:" || urlObj.protocol === "https:";
    } catch {
      return false;
    }
  };

  const handleExtract = async () => {
    if (!validateUrl(url)) {
      setError("Please enter a valid URL");
      return;
    }

    if (!agentId) {
      setError("Agent ID is required");
      notify("Agent ID not found. Please ensure you're logged in.", { type: "error" });
      return;
    }

    setLoading(true);
    setError("");
    setSuccess(false);
    setResult(null);

    try {
      const response = await extractTextApi.extractText(
        {
          extractRequest: { 
            url,
            agent_id: agentId 
          },
        },
        {
          timeout: 1800000, // 30 minutes timeout (1800 seconds)
        }
      );

      const data = response.data;
      setResult(data);
      
      if (data.saved > 0) {
        setSuccess(true);
        
        // Build success message
        let message = `Success! Saved ${data.saved} properties`;
        if (data.failed > 0) {
          message += `, ${data.failed} failed`;
        }
        if (data.total_listings) {
          message += ` from ${data.total_listings} listings`;
        }
        
        notify(message, { type: "success" });
        
        // Refresh the data list
        setTimeout(() => {
          refresh();
        }, 1000);
      } else if (data.failed > 0) {
        setError(`Failed to save properties. Please try again or check logs.`);
        notify("All properties failed to save", { type: "error" });
      } else {
        setError("No properties were extracted from the URL");
        notify("No data extracted from URL", { type: "warning" });
      }
      
    } catch (err: any) {
      console.error("Extract error:", err);
      
      // Better error messages
      let errorMsg = "An error occurred while processing";
      
      if (err?.code === "ECONNABORTED" || err?.message?.includes("timeout")) {
        errorMsg = "Request timed out. The process may still be running in the background.";
      } else if (err?.response?.status === 500) {
        errorMsg = "Server error occurred. Please check backend logs.";
      } else if (err?.response?.status === 400) {
        errorMsg = err?.response?.data?.detail || "Invalid request";
      } else if (err?.message) {
        errorMsg = err.message;
      }
      
      setError(errorMsg);
      notify(errorMsg, { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  // 🔥 CRITICAL FIX: Prevent modal close during processing
  const handleClose = () => {
    if (loading) {
      // Show warning but don't close
      notify("Please wait for the process to complete", { type: "warning" });
      return;
    }
    
    // Only close if not loading
    setUrl("");
    setError("");
    setSuccess(false);
    setResult(null);
    onClose();
  };

  const handleTryAgain = () => {
    setError("");
    setSuccess(false);
    setResult(null);
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} // 🔥 This now checks loading state
      maxWidth="md" 
      fullWidth
      disableEscapeKeyDown={loading} // 🔥 Prevent ESC key during loading
      // 🔥 NEW: Prevent backdrop click during loading
      onBackdropClick={(e: React.MouseEvent<HTMLDivElement>) => {
        if (loading) {
          e.stopPropagation();
          e.preventDefault();
          notify("Please wait for the process to complete", { type: "warning" });
        }
      }}
      // 🔥 NEW: Additional prop to prevent all closes during loading
      PaperProps={{
        onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
          if (loading && e.key === 'Escape') {
            e.stopPropagation();
            e.preventDefault();
          }
        }
      }}
    >
      <DialogTitle>
        Import Properties from URL
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
          Supports PropertyGuru agent pages and single listings
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <TextField
            fullWidth
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.propertyguru.com.sg/property-for-sale?listedById=..."
            disabled={loading || success}
            sx={{ mb: 2 }}
            label="URL"
            variant="outlined"
            helperText="Paste a PropertyGuru agent page or single listing URL"
          />
          
          <Button
            onClick={handleExtract}
            variant="contained"
            disabled={!url.trim() || loading || success}
            startIcon={loading ? <CircularProgress size={20} /> : <LinkIcon />}
            sx={{ mb: 2 }}
            fullWidth
            size="large"
          >
            {loading ? "Processing..." : "Extract & Save Properties"}
          </Button>

          {loading && (
            <Box sx={{ mt: 2, mb: 2 }}>
              <LinearProgress />
              <Box sx={{ 
                mt: 2, 
                p: 2, 
                bgcolor: 'info.main', 
                color: 'white', 
                borderRadius: 1 
              }}>
                <Typography variant="body2" gutterBottom>
                  <strong>⏳ Processing in progress...</strong>
                </Typography>
                <Typography variant="caption" component="div" sx={{ mt: 1 }}>
                  {url.toLowerCase().includes("propertyguru") && (
                    <>
                      • Scraping property data from PropertyGuru
                      <br />
                    </>
                  )}
                  • Extracting details using AI
                  <br />
                  • Saving automatically to database
                  <br />
                  <br />
                  <strong>This may take 5-10 minutes for agent pages with multiple listings.</strong>
                  <br />
                  <strong>⚠️ Please keep this window open! Do not close or refresh.</strong>
                </Typography>
              </Box>
            </Box>
          )}

          {error && !success && (
            <Alert 
              severity="error" 
              icon={<ErrorIcon />}
              sx={{ mt: 2 }}
              action={
                <Button color="inherit" size="small" onClick={handleTryAgain}>
                  Try Again
                </Button>
              }
            >
              <Typography variant="subtitle2" gutterBottom>
                ❌ Error
              </Typography>
              <Typography variant="body2">
                {error}
              </Typography>
            </Alert>
          )}

          {success && result && (
            <Alert 
              severity="success" 
              icon={<CheckCircleIcon />} 
              sx={{ mt: 2 }}
            >
              <Typography variant="subtitle2" gutterBottom>
                ✅ Import Completed Successfully!
              </Typography>
              <Box sx={{ mt: 1 }}>
                {result.total_listings && (
                  <Typography variant="body2">
                    📄 Listings Processed: <strong>{result.total_listings}</strong>
                  </Typography>
                )}
                {result.total_properties !== undefined && result.total_properties > 0 && (
                  <Typography variant="body2">
                    🏠 Properties Found: <strong>{result.total_properties}</strong>
                  </Typography>
                )}
                <Typography variant="body2">
                  ✅ Successfully Saved: <strong>{result.saved}</strong>
                </Typography>
                {result.failed > 0 && (
                  <Typography variant="body2" color="warning.main">
                    ⚠️ Failed: <strong>{result.failed}</strong>
                  </Typography>
                )}
              </Box>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                All properties have been saved to your database. You can now close this dialog.
              </Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {success && (
          <Button 
            onClick={handleTryAgain} 
            color="primary"
            disabled={loading}
          >
            Import Another URL
          </Button>
        )}
        <Button 
          onClick={handleClose} 
          startIcon={<CloseIcon />}
          disabled={loading} // 🔥 Disable close button during loading
          variant={success ? "contained" : "outlined"}
        >
          {success ? "Done" : "Cancel"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MultiTabURLImportModal;










// import React, { useState } from "react";
// import {
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   Tabs,
//   Tab,
//   Box,
//   Button,
//   CircularProgress,
//   Alert,
//   TextField,
//   Chip,
// } from "@mui/material";
// import { useNotify, useRefresh } from "react-admin";
// import { Link as LinkIcon, Save as SaveIcon, Close as CloseIcon } from "@mui/icons-material";
// import { extractTextApi, customerDataApi } from "../providers/env"; // Adjust path as needed
// import { useParams, Params } from "react-router-dom";
// import axios from "axios";

// interface MultiTabURLImportModalProps {
//   open: boolean;
//   onClose: () => void;
// }

// const defaultTagOptions = [
//   "imported",
//   "url-extracted",
//   "defaultTag",
//   "deposit",
//   "tag",
// ];

// const MultiTabURLImportModal: React.FC<MultiTabURLImportModalProps> = ({
//   open,
//   onClose,
// }) => {
//   const [url, setUrl] = useState("");
//   const [loading, setLoading] = useState(false);
//   const [savingAll, setSavingAll] = useState(false);
//   const [error, setError] = useState("");
//   const [results, setResults] = useState<{ title: string; description: string }[]>([]);
//   const [activeTab, setActiveTab] = useState(0);
//   const notify = useNotify();
//   const refresh = useRefresh();
//   const params = useParams<Params>();

//   // Get agent ID from various sources
//   const agentIdFromParams = params?.agentId;
//   const agentId =
//     agentIdFromParams ??
//     (() => {
//       try {
//         return localStorage.getItem("agentId");
//       } catch {
//         return undefined;
//       }
//     })();

//   // Form state for each tab
//   const [formStates, setFormStates] = useState<
//     { key: string; value: string; tags: string[] }[]
//   >([]);

//   const validateUrl = (url: string): boolean => {
//     console.log(agentId)
//     try {
//       const urlObj = new URL(url);
//       return urlObj.protocol === "http:" || urlObj.protocol === "https:";
//     } catch {
//       return false;
//     }
//   };

//   const handleExtract = async () => {
//     if (!validateUrl(url)) {
//       setError("Please enter a valid URL");
//       return;
//     }

//     setLoading(true);
//     setError("");
//     setResults([]);
//     setActiveTab(0);

//     try {
//       const response = await extractTextApi.extractText({
//         extractRequest: { url },
//       });

//       const resultList = response.data.list_of_properties;
//       if (Array.isArray(resultList) && resultList.length > 0) {
//         setResults(resultList);
//         setFormStates(
//           resultList.map((item) => ({
//             key: item.title || "",
//             value: item.description || "",
//             tags: ["imported", "url-extracted"],
//           }))
//         );
//         setError("");
//       } else {
//         setError("No data extracted from URL");
//       }
//     } catch (err) {
//       setError("Network error. Please try again.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleClose = () => {
//     setUrl("");
//     setError("");
//     setResults([]);
//     setFormStates([]);
//     setActiveTab(0);
//     onClose();
//   };

//   const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
//     setActiveTab(newValue);
//   };

//   // Handle form field change for active tab
//   const handleFormChange = (field: string, value: any) => {
//     setFormStates((prev) =>
//       prev.map((form, idx) =>
//         idx === activeTab ? { ...form, [field]: value } : form
//       )
//     );
//   };

//   // Handle tag add/remove
//   const handleTagToggle = (tag: string) => {
//     setFormStates((prev) =>
//       prev.map((form, idx) =>
//         idx === activeTab
//           ? {
//               ...form,
//               tags: form.tags.includes(tag)
//                 ? form.tags.filter((t) => t !== tag)
//                 : [...form.tags, tag],
//             }
//           : form
//       )
//     );
//   };

//   // Validate all forms
//   const validateAllForms = (): boolean => {
//     for (let i = 0; i < formStates.length; i++) {
//       const form = formStates[i];
//       if (!form.key.trim() || !form.value.trim()) {
//         setActiveTab(i); // Switch to the problematic tab
//         setError(`Please fill both Question and Answer in Record ${i + 1}`);
//         return false;
//       }
//     }
//     return true;
//   };

//   // Save all tabs data
//   const handleSaveAll = async () => {
//     if (!agentId) {
//       notify("Agent ID is required", { type: "error" });
//       return;
//     }

//     if (!validateAllForms()) {
//       return;
//     }

//     setSavingAll(true);
//     setError("");
//     let successful = 0;
//     let failed = 0;
//     const errors: string[] = [];

//     try {
//       for (let i = 0; i < formStates.length; i++) {
//         const form = formStates[i];
//         try {
//           await customerDataApi.createCustomerData({
//             customerDataCreate: {
//               key: form.key,
//               value: form.value,
//               tags: form.tags,
//               agent_id: agentId,
//             },
//           });
//           successful++;
//         } catch (err) {
//           failed++;
//           errors.push(`Record ${i + 1}: ${err instanceof Error ? err.message : 'Unknown error'}`);
//           console.error(`Error creating customer data for record ${i + 1}:`, err);
//         }
//       }

//       // Show results notification
//       if (successful === formStates.length) {
//         notify(`Successfully saved all ${successful} records!`, { type: "success" });
//         handleClose();
//         refresh();
//       } else if (successful > 0) {
//         notify(`Saved ${successful} records, ${failed} failed. Check console for details.`, { type: "warning" });
//         // Remove successful records or handle partial success as needed
//       } else {
//         notify("Failed to save any records", { type: "error" });
//         setError(`All records failed to save. First error: ${errors[0]}`);
//       }
//     } catch (err) {
//       notify("Unexpected error occurred", { type: "error" });
//       setError("Unexpected error occurred while saving");
//     } finally {
//       setSavingAll(false);
//     }
//   };

//   // Submit handler for active tab (individual save - keeping for backward compatibility)
//   const handleSubmit = async (e: React.FormEvent) => {
//     e.preventDefault();
//     const form = formStates[activeTab];
    
//     // Check if agentId is available
//     if (!agentId) {
//       notify("Agent ID is required", { type: "error" });
//       return;
//     }

//     if (!form.key.trim() || !form.value.trim()) {
//       setError("Please fill both Question and Answer");
//       return;
//     }

//     try {
//       const CustomerDataApi = await customerDataApi.createCustomerData({
//         customerDataCreate: {
//           key: form.key,
//           value: form.value,
//           tags: form.tags,
//           agent_id: agentId,
//         },
//       });

//       notify("Created successfully", { type: "success" });

//       // Remove the saved tab from results and formStates
//       setResults((prevResults) => {
//         const newResults = prevResults.filter((_, idx) => idx !== activeTab);
//         // If no tabs left, close modal
//         if (newResults.length === 0) {
//           handleClose();
//           refresh();
//         } else {
//           // Adjust activeTab if needed
//           setActiveTab((prevTab) => (prevTab >= newResults.length ? newResults.length - 1 : prevTab));
//         }
//         return newResults;
//       });
//       setFormStates((prevFormStates) => prevFormStates.filter((_, idx) => idx !== activeTab));
//     } catch (err) {
//       notify("Error while creating context", { type: "error" });
//       console.error("Error creating customer data:", err);
//     }
//   };

//   return (
//     <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
//       <DialogTitle>Import from URL</DialogTitle>
//       <DialogContent>
//         <Box sx={{ pt: 1 }}>
//           <TextField
//             fullWidth
//             value={url}
//             onChange={(e) => setUrl(e.target.value)}
//             placeholder="Paste URL here"
//             disabled={loading || savingAll}
//             sx={{ mb: 2 }}
//           />
//           <Button
//             onClick={handleExtract}
//             variant="contained"
//             disabled={!url.trim() || loading || savingAll}
//             startIcon={loading ? <CircularProgress size={20} /> : <LinkIcon />}
//             sx={{ mb: 2 }}
//           >
//             {loading ? "Extracting..." : "Extract"}
//           </Button>
//           {error && (
//             <Alert severity="error" sx={{ mt: 2 }}>
//               {error}
//             </Alert>
//           )}
//         </Box>
//         {results.length > 0 && (
//           <Box sx={{ mt: 3 }}>
//             <Tabs
//               value={activeTab}
//               onChange={handleTabChange}
//               variant="scrollable"
//               scrollButtons="auto"
//             >
//               {results.map((_, idx) => (
//                 <Tab key={idx} label={`Record ${idx + 1}`} />
//               ))}
//             </Tabs>
//             <Box sx={{ mt: 2 }}>
//               {/* Custom Form for each tab */}
//               <form onSubmit={handleSubmit}>
//                 <TextField
//                   label="Question"
//                   fullWidth
//                   multiline
//                   minRows={2}
//                   value={formStates[activeTab]?.key || ""}
//                   onChange={(e) => handleFormChange("key", e.target.value)}
//                   sx={{ mb: 2 }}
//                   required
//                   disabled={savingAll}
//                 />
//                 <TextField
//                   label="Answer"
//                   fullWidth
//                   multiline
//                   minRows={3}
//                   value={formStates[activeTab]?.value || ""}
//                   onChange={(e) => handleFormChange("value", e.target.value)}
//                   sx={{ mb: 2 }}
//                   required
//                   disabled={savingAll}
//                 />
//                 <Box sx={{ mb: 2 }}>
//                   {defaultTagOptions.map((tag) => (
//                     <Chip
//                       key={tag}
//                       label={tag}
//                       color={
//                         formStates[activeTab]?.tags.includes(tag)
//                           ? "primary"
//                           : "default"
//                       }
//                       onClick={() => !savingAll && handleTagToggle(tag)}
//                       disabled={savingAll}
//                       sx={{ mr: 1, mb: 1, cursor: savingAll ? "default" : "pointer" }}
//                     />
//                   ))}
//                 </Box>
//                 <Box sx={{ display: 'flex', gap: 2 }}>
//                   <Button 
//                     type="submit" 
//                     variant="outlined" 
//                     color="primary"
//                     disabled={savingAll}
//                   >
//                     Save This Record
//                   </Button>
//                   <Button
//                     onClick={handleSaveAll}
//                     variant="contained"
//                     color="success"
//                     disabled={savingAll}
//                     startIcon={savingAll ? <CircularProgress size={20} /> : <SaveIcon />}
//                   >
//                     {savingAll ? "Saving All..." : `Save All Records (${formStates.length})`}
//                   </Button>
//                 </Box>
//               </form>
//             </Box>
//           </Box>
//         )}
//       </DialogContent>
//       <DialogActions>
//         <Button 
//           onClick={handleClose} 
//           startIcon={<CloseIcon />}
//           disabled={loading || savingAll}
//         >
//           Close
//         </Button>
//       </DialogActions>
//     </Dialog>
//   );
// };

// export default MultiTabURLImportModal;